import React, { useState, useEffect } from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import {
  handleApiError as handleApiErrorRaw,
  handleNetworkError as handleNetworkErrorRaw,
} from '@utils/apiErrorHandler';

// Loose-cast .jsx util signatures so JSDoc-derived "all required" param shapes don't reject our partial options
const handleApiError = handleApiErrorRaw as (response: unknown, options?: Record<string, unknown>) => void;
const handleNetworkError = handleNetworkErrorRaw as (error: unknown, options?: Record<string, unknown>) => void;
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Space,
  Descriptions,
  Modal
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRevalidator } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

const { TextArea } = Input;

interface MerchantOption {
  id: string;
  name: string;
}

interface Brand {
  id: string;
  name: string;
  description?: string | null;
  merchant_id?: string;
  editable?: boolean;
  deletable?: boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface BrandService {
  // 服務來源混雜（admin: .js any、operator: .ts Zod-inferred 具體 shape）— return 一律 Promise<any> 接收
  get: (id: string) => Promise<any>;
  update: (id: string, values: any) => Promise<any>;
  delete: (id: string) => Promise<any>;
  [key: string]: unknown;
}

interface BrandShowProps {
  brand: Brand | null;
  brandService: BrandService;
  role?: 'admin' | 'operator';
  merchants?: MerchantOption[];
  basePath?: string;
}

const BrandShow: React.FC<BrandShowProps> = ({
  brand: initialBrand,
  brandService,
  role = 'admin',
  merchants = [],
  basePath = '/brands'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [brand, setBrand] = useState<Brand | null>(initialBrand);

  // Sync loader data changes
  useEffect(() => {
    if (initialBrand) {
      setBrand(initialBrand);
      form.setFieldsValue(initialBrand);
    }
  }, [initialBrand, form]);

  const handleEdit = () => {
    if (brand?.editable === false) {
      message.warning(t('common.error.permission_denied') as string);
      return;
    }
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    form.setFieldsValue(brand);
  };

  const handleSave = async (values: Record<string, unknown>) => {
    if (!brand) return;
    setSaving(true);
    try {
      const response = await brandService.update(brand.id, values);
      if (response.success) {
        message.success(t('common.message.update_success') as string);
        if (response.data) setBrand(response.data);
        setIsEditMode(false);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.update_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Update brand error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!brand) return;
    if (brand?.deletable === false) {
      message.warning(t('common.error.permission_denied') as string);
      return;
    }
    Modal.confirm({
      title: t('shared.brands.detail.delete_confirm') as string,
      icon: <ExclamationCircleOutlined />,
      content: t('shared.brands.detail.delete_confirm_description') as string,
      okText: t('common.button.confirm') as string,
      okType: 'danger',
      cancelText: t('common.button.cancel') as string,
      onOk: async () => {
        setDeleting(true);
        try {
          const response = await brandService.delete(brand.id);
          if (response.success) {
            message.success(t('common.message.delete_success') as string);
            navigate(basePath);
          } else {
            handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') });
          }
        } catch (error) {
          handleNetworkError(error, { t });
          console.error('Delete brand error:', error);
        } finally {
          setDeleting(false);
        }
      }
    });
  };

  const getMerchantName = (merchantId?: string) => {
    if (!merchantId || merchants.length === 0) return '-';
    return merchants.find((merchant) => merchant.id === merchantId)?.name || '-';
  };

  if (!brand) {
    return (
      <Card>
        <p>{t('shared.brands.detail.not_found')}</p>
      </Card>
    );
  }

  const showEditButton = brand?.editable !== false;
  const showDeleteButton = brand?.deletable !== false;
  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  const headerActions = !isEditMode && (
    <Space>
      {showEditButton && (
        <Can requiredPermission="brands:write">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            {t('common.button.edit')}
          </Button>
        </Can>
      )}
      {showDeleteButton && (
        <Can requiredPermission="brands:write">
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDelete}
            loading={deleting}
          >
            {t('common.button.delete')}
          </Button>
        </Can>
      )}
    </Space>
  );

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['products', 'products.brands', { label: brand.name }]}
        actions={headerActions}
      />
      {isEditMode ? (
          <>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={brand}
            >
              <Form.Item
                label={t('models.brand.name')}
                name="name"
                rules={[
                  { required: true, message: t('shared.brands.form.name_required') as string }
                ]}
              >
                <Input placeholder={t('shared.brands.form.name_placeholder') as string} />
              </Form.Item>

              <Form.Item
                label={t('models.brand.description')}
                name="description"
              >
                <TextArea
                  rows={4}
                  placeholder={t('shared.brands.form.description_placeholder') as string}
                />
              </Form.Item>

              <FloatingActionBarSpacer />
            </Form>
            <FloatingActionBar
              role={role}
              onCancel={handleCancel}
              onSubmit={() => form.submit()}
              loading={saving}
            />
          </>
        ) : (
          <Descriptions column={1} bordered>
            {merchants.length > 0 && brand.merchant_id && (
              <Descriptions.Item label={t('models.brand.merchant')}>
                {getMerchantName(brand.merchant_id)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('models.brand.name')}>
              {brand.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.brand.description')}>
              {brand.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {brand.created_at ? formatDateTime(brand.created_at) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.updated_at')}>
              {brand.updated_at ? formatDateTime(brand.updated_at) : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
    </div>
  );
};

export default BrandShow;
